/**
 * @ Author: Housefolios
 * @ Create Time: 2022-05-12 17:58:20
 * @ Modified by: David Helmick
 * @ Modified time: 2025-10-27 10:42:39
 * @ Description: Dialog to change the viewable strategies of properties
 */

import { editStrategyAddIconSx } from '@/theme/skinSx'
import React, { useState } from 'react'

// Centralized lodash imports - much cleaner!
import {
  map,
  head,
  cloneDeep,
  merge,
  filter,
  includes,
  isEmpty,
} from '@/utils/lodash'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  Box,
} from '@mui/material'

import { useMutation } from '@apollo/client/react'
import { GET_PROPERTIES } from '@/housefolios-components/Properties/queries'
import { enqueueSnackbar } from 'notistack'
import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'
import withSession from '@/housefolios-components/Session/withSession'
import { UPDATE_LOT } from '@/housefolios-components/Lots/mutations'
import { UPDATE_PROPERTIES } from '@/housefolios-components/Portfolio/mutations'
import { GET_PORTFOLIOS } from '@/housefolios-components/Portfolio/queries'
import { GET_ME } from '@/housefolios-components/Session/queries'
import { GET_LOT } from '@/housefolios-components/Lots/queries'
import { DEFAULT_PREFERENCES } from '@/housefolios-components/OrganizationSettings/OrganizationPreferences'
import Autocomplete from '@mui/material/Autocomplete'
import { GET_PROPERTIES_QUERY_LIMIT } from '@/utils/pagination'
import {
  filterPreferedStrategies,
  getStrategyName,
} from '@/utils/stratagies'

function EditStrategyComponent(props) {
  const { property = {}, editProperty, isSidebar } = props
  const [openEditStrategy, setEditStrategy] = useState(false)
  const [loader, setLoader] = useState(false)

  const preferences = merge(
    cloneDeep(DEFAULT_PREFERENCES),
    props?.session?.me?.activeOrg?.memberPreferences,
    props?.session?.me?.activeOrg?.preferences,
    {
      general: {
        strategies: property.portfolio
          ? property.portfolio.strategies
          : undefined,
      },
    },
  )
  const [strategies, setStrategies] = useState(
    filterPreferedStrategies(
      preferences,
      props?.session?.me?.activeOrg?.member,
      true,
    ),
  )

  const handleOpenEditStrategy = () => {
    if (!isEmpty(property.strategies))
      setStrategies(property.strategies)
    setEditStrategy(true)
  }
  const handleCloseEditStrategy = () => {
    setEditStrategy(false)
    setLoader(false)
  }

  const clearBulkSelection = () => {
    if (props.setSelected) props.setSelected([])
  }

  const [updateProperties] = useMutation(UPDATE_PROPERTIES)

  const changeProperties = () => {
    updateProperties({
      variables: {
        ids: props.selected,
        propertyInput: {
          strategies,
          strategy: editProperty
            ? includes(strategies, property.strategy)
              ? property.strategy
              : head(strategies)
            : null,
        },
      },
      refetchQueries: [
        {
          query: GET_PROPERTIES,
          variables: {
            portfolioId: props.selectedPortfolio
              ? [props.selectedPortfolio._id]
              : null,
            limit: GET_PROPERTIES_QUERY_LIMIT,
          },
        },
        {
          query: GET_PROPERTIES,
          variables: {
            portfolioId: map(
              props.portfolios,
              (portfolio) => portfolio._id,
            ),
          },
        },
        {
          query: GET_PORTFOLIOS,
        },
        {
          query: GET_ME,
        },
      ],
    })
      .then(() => {
        enqueueSnackbar(`Financial Analysis Changes Saved`, {
          variant: 'success',
          autoHideDuration: 3000,
        })
        handleCloseEditStrategy()
        clearBulkSelection()
      })
      .catch(() => {
        enqueueSnackbar(`Financial Analysis failed to Change`, {
          variant: 'error',
          autoHideDuration: 3000,
        })
        handleCloseEditStrategy()
      })
  }

  return (
    <>
      {editProperty && (
        <Grid
          size={{ xs: 12, lg: isSidebar ? 12 : 4 }}
          onClick={handleOpenEditStrategy}
          data-tour="verifyFinancials-step-2"
          data-hf={isSidebar ? undefined : 'btn-input-select'}
          style={isSidebar ? undefined : { width: '100%', padding: 8 }}
          sx={
            isSidebar
              ? {
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  py: 1,
                  cursor: 'pointer',
                }
              : [{
                border: "#e6e7f1 solid 2px",
                borderRadius: "0.65rem",
                transition: "all 0.2s ease-in-out",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                width: "100%",
                boxSizing: "border-box",
                textAlign: "left",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                padding: "0.75rem 1rem",
                "& .selected-icon, & [data-hf=\"selected-icon\"]": {
                  opacity: 0,
                  borderRadius: "100%",
                  visibility: "hidden",
                  width: 28,
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease-in-out",
                  background: "#1bc943",
                  color: "#fff",
                  position: "absolute",
                  right: -12,
                  top: -12,
                  border: "#fff solid 2px",
                },
                "&:hover": { borderColor: "#4f5062", color: "#000 !important", background: "#f8f9ff" },
                "&.active, &[data-hf-selected]": {
                  borderColor: "#1bc943",
                  background: "#fff",
                  "&:hover": { background: "#fff" },
                  "& .selected-icon, & [data-hf=\"selected-icon\"]": { opacity: 1, visibility: "visible" },
                },
                "html[data-color-mode=\"dark\"] &": { borderColor: "rgba(255, 255, 255, 0.28)", color: "rgba(255, 255, 255, 0.87)", background: "transparent", "&:hover": { borderColor: "rgba(255, 255, 255, 0.5)", color: "rgba(255, 255, 255, 0.87) !important", background: "#2a2a2a" }, "&.active, &[data-hf-selected]": { borderColor: "#1bc943", background: "#2a2a2a", color: "rgba(255, 255, 255, 0.87)", "&:hover": { background: "#2a2a2a" } } },
              }, { m: 0 }]
          }
        >
          <div
            style={{
              textAlign: 'center',
              ...(isSidebar
                ? { borderRadius: '0.65rem' }
                : { padding: '1.5rem'  }),
            }}
          >
            <Grid
              container
              sx={{
                justifyContent: 'center',
              }}
            >
              <Grid data-hf-grid="keep">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box sx={editStrategyAddIconSx(isSidebar)}>
                    <FontAwesomeIcon icon={['fas', 'plus']} sx={{ fontSize: "1.265rem !important" }} />
                  </Box>
                  {/* <div>
                    <div>
                      Strategies
                    </div>
                    <div>
                      {property.strategy}
                    </div>
                  </div>
                  <Tooltip title="Change Strategy">
                    <div>
                      <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                    </div>
                  </Tooltip> */}
                </Box>
              </Grid>
            </Grid>
          </div>
        </Grid>
        // <FontAwesomeIcon
        //   icon={['fas', 'plus']}
        //
        //   style={{ color: "#368CFF", fontWeight: 600 }}
        //   onClick={handleOpenEditStrategy}
        // />
      )}
      {/* {bulkActionsNotifications && (
         <Grid>
          <Tooltip title="Bulk Edit Strategies">
            <IconButton onClick={handleOpenEditStrategy}>
              <FontAwesomeIcon
                icon={['far', 'chess-rook']}
                style={{ color: 'white' }}
              />
            </IconButton>
          </Tooltip>
        </Grid>
       )} */}
      <Dialog
        open={openEditStrategy}
        onClose={handleCloseEditStrategy}
        maxWidth="xs"
        fullWidth={true}
      >
        <BlockUi
          blocking={loader}
          loader={
            <PacmanLoader color={'var(--primary)'} loading={loader} />
          }
          message={
            <Box sx={{ color: '#368cff' }}>Updating Strategy</Box>
          }
        >
          <DialogTitle id="form-dialog-title">
            Manage Strategies
          </DialogTitle>
          <DialogContent sx={{ p: '1.5rem' }}>
            <Grid
              container
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Grid size={12}>
                <Typography>
                  Choose which strategies you want to see for this
                  property
                </Typography>
              </Grid>
              <Grid size={12}>
                <Autocomplete
                  id="checkboxes-tags-demo"
                  multiple
                  disableCloseOnSelect
                  options={filterPreferedStrategies(
                    preferences,
                    props?.session?.me?.activeOrg?.member,
                    false,
                  )}
                  value={strategies}
                  onChange={(event, newValue) => {
                    if (!isEmpty(newValue)) setStrategies(newValue)
                  }}
                  disableClearable
                  getOptionLabel={(option) =>
                    getStrategyName(
                      option,
                      props?.session?.me?.activeOrg?.member,
                    )
                  }
                  // isOptionEqualToValue={(option, value) =>
                  //   option === value.value && value.active
                  // }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Strategies"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Typography style={{ flex: 1 }} />
            <Button variant="text" onClick={handleCloseEditStrategy}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              style={{ marginRight: 10 }}
              onClick={() => {
                setLoader(true)
                changeProperties()
              }}
            >
              Save
            </Button>
          </DialogActions>
        </BlockUi>
      </Dialog>
    </>
  )
}

export default withSession(EditStrategyComponent)
